import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { FaCalendarAlt, FaClock, FaArrowRight } from 'react-icons/fa';
import blogManifest from '../data/blogManifest.json';

export type BlogPublicationStatus = 'draft' | 'published';

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  dateModified?: string;
  author: string;
  heroColor: string;
  keywords: string;
  status: BlogPublicationStatus;
  tracksAttribution?: boolean;
  productClaimIds: string[];
}

export const ALL_BLOG_POSTS = blogManifest.posts as BlogPost[];

export const publishedBlogPosts = (posts: BlogPost[]): BlogPost[] => (
  posts.filter(post => post.status === 'published')
);

export const findPublishedBlogPost = (
  posts: BlogPost[],
  slug: string | undefined,
): BlogPost | undefined => (
  publishedBlogPosts(posts).find(post => post.slug === slug)
);

export const BLOG_POSTS: BlogPost[] = publishedBlogPosts(ALL_BLOG_POSTS);

const BlogPage: React.FC = () => {
  const blogListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'StageCrew Blog',
    description: 'Career tips, salary guides, and industry insights for AV technicians, stagehands, and event staffing professionals.',
    url: 'https://stagecrew.app/blog',
    publisher: {
      '@type': 'Organization',
      name: 'StageCrew',
      url: 'https://stagecrew.app',
    },
    blogPost: BLOG_POSTS.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      url: `https://stagecrew.app/blog/${post.slug}`,
      datePublished: post.date,
      dateModified: post.dateModified || post.date,
      author: { '@type': 'Organization', name: post.author },
    })),
  };

  return (
    <div>
      <SEOHead
        title="Blog — StageCrew | AV & Stagehand Industry Insights"
        description="Career tips, salary guides, and industry insights for AV technicians, stagehands, and event staffing professionals."
        url="https://stagecrew.app/blog"
        canonical="https://stagecrew.app/blog"
        keywords="stagehand blog, AV technician career tips, event staffing insights, stagehand salary guide"
        jsonLd={blogListJsonLd}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-800 via-indigo-700 to-purple-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">StageCrew Blog</h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            Career guidance, rate-checking factors, and industry workflow insights for AV techs, stagehands, and employers.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {BLOG_POSTS.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition group"
              >
                {/* Color bar */}
                <div className={`h-2 bg-gradient-to-r ${post.heroColor}`} />
                <div className="p-6">
                  <span className="inline-block text-xs font-semibold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full mb-3">
                    {post.category}
                  </span>
                  <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <FaCalendarAlt className="text-gray-300" />
                      {new Date(post.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        timeZone: 'UTC',
                      })}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaClock className="text-gray-300" />
                      {post.readTime}
                    </span>
                  </div>
                  <div className="mt-4 flex items-center text-blue-600 text-sm font-medium group-hover:gap-2 transition-all">
                    Read More <FaArrowRight className="ml-1 text-xs" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-16 bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
        data-content-truth="neutral-blog-index-cta"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Keep Learning</h2>
          <p className="text-lg text-indigo-100 mb-8">
            Read practical guides for live-event workers and production teams.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register?role=worker"
              className="bg-white text-indigo-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg"
            >
              Worker Resources
            </Link>
            <Link
              to="/for-employers"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:bg-opacity-10 transition"
            >
              Employer Resources
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
